import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, DoCheck,
  ElementRef, EventEmitter,
  Input, OnInit,
  Optional, Output,
  Renderer2, Self,
  ViewEncapsulation,
} from '@angular/core';
import {ControlValueAccessor, FormControl, NgControl} from '@angular/forms';
import {ErrorStateMatcher} from '@angular/material/core';
import moment from 'moment';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {DatePickerHeaderComponent} from './date-picker-header/date-picker-header.component';
import {ViewChild} from '@angular/core';
import {MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';

class CustomFieldErrorMatcher implements ErrorStateMatcher {
  constructor(private customControl: FormControl) {}
  isErrorState(): boolean {
    return this.customControl && this.customControl.touched && (this.customControl.invalid);
  }
}

@Component({
  selector: 'app-date-picker',
  templateUrl: './app-date-picker.component.html',
  styleUrls: ['./app-date-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppDatePickerComponent implements OnInit, AfterViewInit, ControlValueAccessor, DoCheck {
  @Input() public filterDateFn?: (d: Date | null) => boolean;
  @Input() public inputLabel: string;
  @Input() public shouldShowLabel = true;
  @Input() public inputHint?: string;
  @Input() public warningMessage?: string;
  @Input() public isWarningVisible?: boolean;
  @Input() public testId?: string;
  @Input() public disabled?: boolean;
  @Input() public useRequiredProperty = true;

  /**
   * Html form validation flag
   */
  @Input() isRequired = true;

  @Output() public dateSelected: EventEmitter<Date>
    = new EventEmitter<Date>();

  /**
   * Restrict date selection that out of the project scope
   */
  @Input() public minDate = moment('1900-01-01 00:00:00', 'YYYY-MM-DD HH:mm:ss').toDate();
  @Input() public maxDate = moment('2199-12-31 23:59:59', 'YYYY-MM-DD HH:mm:ss').toDate();
  @ViewChild('datePickerToggle', {
    read: MatDatepickerToggle,
    static: false
  }) datePickerToggle: MatDatepickerToggle<any>;
  @ViewChild(MatDatepickerInput, {
    read: MatDatepickerInput
  }) datePickerInput: MatDatepickerInput<unknown>;

  private ngUnsubscribe = new Subject<void>();
  public hasFocusInitial = false;
  private oldTouched = null;
  private oldValid = null;
  private oldDisabled = null;

  constructor(private element: ElementRef,
              private renderer: Renderer2,
              private cd: ChangeDetectorRef,
              @Self() @Optional() public ctrl: NgControl) {
    if (this.ctrl) {
      this.ctrl.valueAccessor = this;
    }
    this.hasFocusInitial = element.nativeElement.getAttribute('cdkFocusInitial') != null;
    renderer.removeAttribute(element.nativeElement, 'cdkFocusInitial');
  }

  public selectedDate: Date;
  datePickerHeaderHeader = DatePickerHeaderComponent;

  private toggleElement: HTMLElement;

  onChange: any = (value: string) => { };
  onTouched: any = () => {};

  ngDoCheck() {
    if (this.ctrl.touched !== this.oldTouched) {
      this.oldTouched = this.ctrl.touched;
      this.cd.markForCheck();
    }
    if (this.ctrl.valid !== this.oldValid) {
      this.oldValid = this.ctrl.valid;
      this.cd.markForCheck();
    }
    if (this.ctrl.disabled !== this.oldDisabled) {
      this.oldDisabled = this.ctrl.disabled;
      if (this.datePickerToggle) {
        this.datePickerToggle.disabled = this.ctrl.disabled;
        this.datePickerInput.setDisabledState(this.ctrl.disabled);
        (this.datePickerToggle as any)?._changeDetectorRef?.markForCheck();
      }
      this.cd.markForCheck();
    }
  }

  ngOnInit() {
    this.testId = this.testId || 'date-picker-toggle';

    if (this.ctrl.control) {
      this.ctrl.control.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((newDate: Date) => {
        this.dateSelected.emit(newDate);
      });
      this.ctrl.control.statusChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => this.cd.markForCheck());
    }
  }

  ngAfterViewInit(): void {
    this.toggleElement = this.element.nativeElement.querySelector(`[data-test-id=${this.testId}]`);
  }

  public focusInput(): void {
    this.renderer.addClass(this.toggleElement, 'mat-datepicker-toggle-active');
  }

  public blurInput(): void {
    this.renderer.removeClass(this.toggleElement, 'mat-datepicker-toggle-active');
  }

  writeValue(value: any): void {
    this.cd.markForCheck();
  }

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState?(disabled: boolean): void {
    this.disabled = disabled;
  }

  public errorMatcher(): CustomFieldErrorMatcher {
    return new CustomFieldErrorMatcher(this.ctrl.control as FormControl);
  }
}
